/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'

import { EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { RemoteResourceRef } from '@modules/cloud-api/v1/types'
import type { StackDeploymentCreateRequest } from '@modules/ui-types'
import type { DeepPartial } from '@modules/ts-essentials'

import { getUpsertVersion } from '@/lib/stackDeployments/selectors/creates'
import { getEsPlan } from '@/lib/stackDeployments/selectors/stackDeployment'

import CcsRemoteDeploymentsTable from '../../CcsRemoteDeploymentsTable'
import { markShallow } from '../../../lib/immutability-helpers'

export type Props = {
  editorState: StackDeploymentCreateRequest
  onChange: (
    changes: DeepPartial<StackDeploymentCreateRequest>,
    settings?: { shallow?: boolean },
  ) => void
}

class CcsSetupRemoteDeployments extends Component<Props> {
  render(): JSX.Element {
    const { editorState } = this.props
    const {
      deployment: { name },
    } = editorState
    const version = getUpsertVersion(editorState)
    const remoteDeployments = this.getRemoteDeployments()

    return (
      <Fragment>
        <EuiText color='subdued'>
          <FormattedMessage
            id='setup-associated-deployments.description'
            defaultMessage='The {deploymentName} deployment can read data from other deployments. Specify which deployments to search across.'
            values={{
              deploymentName: <strong>{name}</strong>,
            }}
          />
        </EuiText>

        <CcsRemoteDeploymentsTable
          deploymentVersion={version!}
          remoteDeployments={remoteDeployments}
          onChange={this.updateRemoteDeployments}
        />
      </Fragment>
    )
  }

  getRemoteDeployments(): RemoteResourceRef[] | null {
    const { editorState } = this.props
    const { deployment } = editorState

    const plan = getEsPlan({ deployment })

    if (!plan?.transient?.remote_clusters) {
      return null
    }

    return plan.transient.remote_clusters.resources
  }

  updateRemoteDeployments = (remoteResources: RemoteResourceRef[]): void => {
    const { onChange } = this.props

    onChange({
      deployment: {
        resources: {
          elasticsearch: [
            {
              plan: {
                transient: {
                  remote_clusters: {
                    resources: markShallow(remoteResources),
                  },
                },
              },
            },
          ],
        },
      },
    })
  }
}

export default CcsSetupRemoteDeployments
